import React, { useCallback, useEffect, useRef, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import AiMaskPaintPreview from "../../shared/Popup/AiMaskPaintPreview";
import AiMaskSelectToolControls, {
  isMaskSelectGenerateReady,
} from "../../shared/Popup/AiMaskSelectToolControls";
import { useAiMaskPaint } from "../../shared/Popup/useAiMaskPaint";
import { removeObjectSingle } from "../../../../../services/api/tools/remove_object";
import { resolveImageSource } from "../../../../../utils/resolveImageSource";

/**
 * AI Tools → AI Remove Object prep (Smart / Brush / Box / Eraser).
 */
const RemoveObjectToolPrep = (props) => {
  const [prompt, setPrompt] = useState("");
  const promptRef = useRef(null);
  const paint = useAiMaskPaint();
  const toolId = paint.paintTool;
  const smartSelected = toolId === "smart";

  useEffect(() => {
    if (smartSelected) promptRef.current?.focus();
  }, [smartSelected]);

  const isGenerateReady = useCallback(
    () =>
      isMaskSelectGenerateReady(toolId, paint.hasMask, {
        prompt,
        promptRequired: toolId === "smart",
      }),
    [toolId, paint.hasMask, prompt]
  );

  const handleGenerate = useCallback(
    async ({ token, file, image_url, resultImageUrl, stageKey }) => {
      const described = prompt.trim() || undefined;
      let mask = null;
      if (toolId !== "smart" && paint.hasMask) {
        const blob = await paint.maskRef.current?.exportMaskBlob?.();
        if (blob) {
          mask = new File([blob], "mask.png", { type: "image/png" });
        }
      }
      const target = toolId === "smart" ? described : undefined;
      if (!target && !mask) {
        throw new Error("Describe what to remove or paint a mask.");
      }

      const editingResult = Boolean(resultImageUrl && stageKey === "preview");
      const input = editingResult
        ? await resolveImageSource({
            sourceUrl: resultImageUrl,
            filename: "remove-object-input.png",
            token,
          })
        : { file, image_url };

      return removeObjectSingle(token, input.file, {
        target,
        mask,
        output_format: "webp",
        ...(input.image_url ? { image_url: input.image_url } : {}),
      });
    },
    [paint.hasMask, paint.maskRef, prompt, toolId]
  );

  return (
    <AiFeatureToolPrep
      {...props}
      onGenerate={handleGenerate}
      generateFileName="remove-object-input.png"
      toolKey="ai_remove_object"
      title="AI Remove Object"
      aka="Object Eraser"
      bio="Erase unwanted objects and fill the gap so the scene looks natural."
      howToSteps={[
        "Upload an image",
        "Paint or box the object to remove on the preview",
        "Tap Generate to fill the gap",
      ]}
      howToTip="Cover the whole object with the mask — edges left uncovered can leave remnants."
      emptyPreviewLabel="Upload an image to preview object removal"
      loadingLabel="Removing object…"
      emptyFooterHint="Upload an image, pick a tool, then generate."
      isGenerateReady={isGenerateReady}
      renderPreview={({
        imageUrl,
        loading,
        loadingLabel,
        emptyLabel,
        hasResult,
      }) => (
        <AiMaskPaintPreview
          ref={paint.maskRef}
          imageUrl={imageUrl}
          loading={loading}
          loadingLabel={loadingLabel}
          emptyLabel={emptyLabel}
          tool={toolId}
          brushSize={paint.brushSize}
          paintEnabled={
            Boolean(imageUrl) && !loading && toolId !== "smart"
          }
          clearVersion={paint.clearVersion}
          onMaskChange={paint.handleMaskChange}
        />
      )}
      renderControls={({ busy, leftPanelRef, sourceImageUrl }) => (
        <>
          <AiMaskSelectToolControls
            paint={paint}
            busy={busy}
            sourceImageUrl={sourceImageUrl}
            leftPanelRef={leftPanelRef}
            subject="object"
          />
          <p className="aftp-hint">
            For removing objects, people and text. Not intended for editing facial features.
          </p>

          <div className="aftp-section">
            <label className="aftp-label" htmlFor="aftp-ro-prompt">
              What to remove
              <span className="aftp-optional">
                {smartSelected ? " (required for Smart)" : " (optional)"}
              </span>
            </label>
            <textarea
              id="aftp-ro-prompt"
              ref={promptRef}
              className={`aftp-input${
                smartSelected && !prompt.trim() ? " aftp-input--highlight" : ""
              }`}
              rows={3}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
              }}
              placeholder="e.g. the person in the background, the cable on the left"
              disabled={busy || !smartSelected}
            />
          </div>
        </>
      )}
    />
  );
};

export default RemoveObjectToolPrep;
